import { useCallback, useEffect, useRef, useState } from 'react';

// "New" on the Clients list = a client that has just become FLAGGED (it is in Needs
// Attention now and was not at the last look). Two jobs:
//
//  1. Poll in the background and say how many newly flagged clients are waiting — the
//     "↑ N new" pill. The poll NEVER touches the list: rows only change when the reader
//     asks (the pill), so nothing reorders under the cursor — the same rule as the Now feed.
//  2. Remember which rows are new, so they can be tinted — until the reader opens that
//     client, or NEW_CLIENT_TTL_MS passes, whichever comes first.
//
// The memory lives in sessionStorage so it survives opening a client and coming back to
// the list (opening a client navigates away and unmounts the page). With no memory yet — the
// first visit of the session — nothing is new: everything flagged is simply what is there.

export const NEW_CLIENT_POLL_MS = 60 * 1000;
export const NEW_CLIENT_TTL_MS = 10 * 60 * 1000;
export const NEW_CLIENTS_STORAGE_KEY = 'hub.clients.new.v1';
// How often the highlight is re-checked for expiry while the page stays open.
const TICK_MS = 30 * 1000;

/** @returns {{known: Set<string>, newAt: Record<string, number>}|null} */
const readStore = () => {
  try {
    const raw = window.sessionStorage.getItem(NEW_CLIENTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.known) || typeof parsed.newAt !== 'object' || parsed.newAt === null) return null;
    return { known: new Set(parsed.known), newAt: { ...parsed.newAt } };
  } catch (e) {
    // Storage blocked or corrupt: behave as a first visit.
    return null;
  }
};

const writeStore = (known, newAt) => {
  try {
    window.sessionStorage.setItem(NEW_CLIENTS_STORAGE_KEY, JSON.stringify({ known: [...known], newAt }));
  } catch (e) {
    /* storage blocked: the highlight just won't survive navigation */
  }
};

/**
 * @param {object} params
 * @param {Set<string>|null} params.flaggedIds  The ids that are flagged in the loaded data; null until it loads.
 * @param {() => Promise<Set<string>>} params.fetchFlaggedIds  Reads the flagged ids afresh (the poll).
 * @param {number} [params.pollMs]
 * @param {number} [params.ttlMs]
 * @param {() => number} [params.now]
 * @returns {{
 *   isNew: (id: string) => boolean,
 *   markOpened: (id: string) => void,
 *   pendingCount: number,
 *   applyPending: () => number,
 * }}
 */
export function useNewClients({
  flaggedIds,
  fetchFlaggedIds,
  pollMs = NEW_CLIENT_POLL_MS,
  ttlMs = NEW_CLIENT_TTL_MS,
  now = Date.now,
}) {
  const knownRef = useRef(new Set());
  const newAtRef = useRef({});
  const initialisedRef = useRef(false);
  // Callers pass fresh closures every render; reading them through refs keeps the effects
  // below keyed on the DATA only (a changing `now` must never re-run the reconcile, which
  // re-renders, which would loop).
  const fetchRef = useRef(fetchFlaggedIds);
  fetchRef.current = fetchFlaggedIds;
  const nowRef = useRef(now);
  nowRef.current = now;
  const ttlRef = useRef(ttlMs);
  ttlRef.current = ttlMs;
  const [pending, setPending] = useState(() => new Set());
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // Reconcile the loaded data: anything flagged that was not known at the last look is
  // new; anything no longer flagged stops being known (so it is new again if it re-flags).
  useEffect(() => {
    if (!flaggedIds) return;
    const stamp = nowRef.current();
    if (!initialisedRef.current) {
      initialisedRef.current = true;
      const stored = readStore();
      if (stored) {
        knownRef.current = stored.known;
        newAtRef.current = stored.newAt;
      } else {
        // First visit this session: nothing to compare against, so nothing is new.
        knownRef.current = new Set(flaggedIds);
      }
    }
    const nextNewAt = {};
    for (const [id, at] of Object.entries(newAtRef.current)) {
      if (flaggedIds.has(id) && stamp - at < ttlRef.current) nextNewAt[id] = at;
    }
    for (const id of flaggedIds) {
      if (!knownRef.current.has(id)) nextNewAt[id] = stamp;
    }
    newAtRef.current = nextNewAt;
    knownRef.current = new Set(flaggedIds);
    writeStore(knownRef.current, newAtRef.current);
    bump();
  }, [flaggedIds, bump]);

  // The background poll: count what is flagged now that this list does not know yet.
  useEffect(() => {
    if (!flaggedIds) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const ids = await fetchRef.current();
        if (cancelled) return;
        const incoming = [...ids].filter((id) => !knownRef.current.has(id));
        setPending((prev) => (
          prev.size === incoming.length && incoming.every((id) => prev.has(id)) ? prev : new Set(incoming)
        ));
      } catch (e) {
        /* a background tick: swallow it, the next one retries */
      }
    }, pollMs);
    return () => { cancelled = true; clearInterval(timer); };
  }, [flaggedIds, pollMs]);

  // Re-check the highlight for expiry while the page stays open.
  useEffect(() => {
    const timer = setInterval(() => {
      if (Object.keys(newAtRef.current).length > 0) bump();
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [bump]);

  const isNew = useCallback((id) => {
    const at = newAtRef.current[id];
    return at != null && nowRef.current() - at < ttlRef.current;
  }, []);

  /** The reader opened this client: it is old from now on. */
  const markOpened = useCallback((id) => {
    if (newAtRef.current[id] == null) return;
    const { [id]: _opened, ...rest } = newAtRef.current;
    newAtRef.current = rest;
    writeStore(knownRef.current, newAtRef.current);
    bump();
  }, [bump]);

  /**
   * The reader asked to see what the poll found: mark those clients new and known. The
   * caller then reloads the list data so the rows actually appear.
   * @returns {number} how many clients were marked
   */
  const applyPending = useCallback(() => {
    const ids = [...pending];
    const stamp = nowRef.current();
    for (const id of ids) {
      newAtRef.current[id] = stamp;
      knownRef.current.add(id);
    }
    writeStore(knownRef.current, newAtRef.current);
    setPending(new Set());
    bump();
    return ids.length;
  }, [pending, bump]);

  return { isNew, markOpened, pendingCount: pending.size, applyPending };
}
